import { ReactNode, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { MoreVertical, Plus } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import FilesTab from '../components/FilesTab'
import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  Dash,
  InlineText,
  InlineDate,
  InlineSelect,
  InlineStatus,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type JobProfile = { id: number; code: string; name: string; is_active: boolean }
type Department = { id: number; code: string; name: string; is_active: boolean }
type Subsidiary = { id: number; code: string; name: string }

type RoleSummary = {
  id: number
  name: string
  description: string
  permission_codes: string[]
}

type Position = {
  id: number
  code: string
  title: string
  job_profile: number | null
  job_profile_code: string | null
  job_profile_name: string | null
  department: number | null
  department_name: string | null
  subsidiary: number | null
  subsidiary_code: string | null
  subsidiary_name: string | null
  manager_position: number | null
  manager_position_code: string | null
  manager_position_title: string | null
  planned_start_date: string | null
  planned_end_date: string | null
  is_active: boolean
  is_open: boolean
  current_employment_id: number | null
  current_holder_name: string | null
  notes: string
  roles: number[]             // list of role IDs (from __all__)
  roles_detail: RoleSummary[] // full objects for display
}

const STATUS_COLOR: Record<'OPEN' | 'FILLED' | 'INACTIVE', string> = {
  OPEN: 'bg-amber-100 text-amber-800',
  FILLED: 'bg-green-100 text-green-800',
  INACTIVE: 'bg-gray-100 text-gray-700',
}

type TabKey = 'overview' | 'roles' | 'files'

export default function HRPositionDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const positionId = id ? Number(id) : 0
  const { confirm, dialog: deleteDialog } = useDeleteConfirm()

  const positionQ = useQuery({
    queryKey: ['hr', 'position', positionId],
    queryFn: async () => (await api.get<Position>(`/hr/positions/${positionId}/`)).data,
    enabled: !!positionId,
  })
  const positionsQ = useQuery({
    queryKey: ['hr', 'positions'],
    queryFn: async () => (await api.get<Position[]>('/hr/positions/')).data,
  })
  // Seats a position may report to: only those held by an eligible
  // people-manager (or vacant). Used for the "Reports to" picker.
  const eligiblePositionsQ = useQuery({
    queryKey: ['hr', 'positions', 'manager-eligible'],
    queryFn: async () =>
      (await api.get<Position[]>('/hr/positions/?manager_eligible=1')).data,
  })
  const jobProfilesQ = useQuery({
    queryKey: ['hr', 'job-profiles'],
    queryFn: async () => (await api.get<JobProfile[]>('/hr/job-profiles/')).data,
  })
  const departmentsQ = useQuery({
    queryKey: ['hr', 'departments'],
    queryFn: async () => (await api.get<Department[]>('/hr/departments/')).data,
  })
  const subsidiariesQ = useQuery({
    queryKey: ['subsidiaries'],
    queryFn: async () => (await api.get<Subsidiary[]>('/subsidiaries/')).data,
  })

  const updateMut = useMutation({
    mutationFn: async (payload: Partial<Position>) =>
      (await api.patch(`/hr/positions/${positionId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'position', positionId] })
      qc.invalidateQueries({ queryKey: ['hr', 'positions'] })
    },
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/hr/positions/${positionId}/`),
    onSuccess: () => {
      toast.success('Position deleted')
      qc.invalidateQueries({ queryKey: ['hr', 'positions'] })
      navigate('/admin/hr/positions')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const [tab, setTab] = useState<TabKey>('overview')

  if (positionQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (positionQ.isError || !positionQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Position not found.</p>
          <Link to="/admin/hr/positions" className="text-blue-600 hover:underline">← Back to positions</Link>
        </div>
      </AdminLayout>
    )
  }

  const position = positionQ.data
  const parentOptions = (eligiblePositionsQ.data || []).filter((p) => p.id !== position.id)
  const jobProfiles = (jobProfilesQ.data || []).filter((j) => j.is_active || j.id === position.job_profile)
  const departments = (departmentsQ.data || []).filter((d) => d.is_active)
  const subsidiaries = subsidiariesQ.data || []
  const directReports = (positionsQ.data || []).filter((p) => p.manager_position === position.id)

  const save = (payload: Partial<Position>) =>
    updateMut.mutateAsync(payload).then(() => undefined)

  const statusKey: 'OPEN' | 'FILLED' | 'INACTIVE' =
    !position.is_active ? 'INACTIVE' : position.is_open ? 'OPEN' : 'FILLED'

  return (
    <AdminLayout>
      {deleteDialog}

      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/hr/positions" className="text-muted-foreground hover:text-foreground hover:underline">← Positions</Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-xs text-muted-foreground">{position.code}</div>
            <h2 className="text-2xl font-bold text-foreground">
              <InlineText value={position.title} onSave={(v) => save({ title: v })} />
            </h2>
            <div className="text-sm text-muted-foreground mt-0.5">
              {position.current_holder_name ? (
                <>Currently held by{' '}
                  {position.current_employment_id ? (
                    <Link to={`/admin/hr/employments/${position.current_employment_id}`} className="text-blue-600 hover:underline">
                      {position.current_holder_name}
                    </Link>
                  ) : (
                    <span>{position.current_holder_name}</span>
                  )}
                </>
              ) : (
                <span>No current holder</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <InlineStatus
              value={statusKey}
              colorMap={STATUS_COLOR}
              options={[
                { value: 'OPEN', label: 'Open' },
                { value: 'FILLED', label: 'Filled' },
                { value: 'INACTIVE', label: 'Inactive' },
              ]}
              // Only `is_active` is editable from here; Open vs Filled is derived.
              onSave={(v) => save({ is_active: v !== 'INACTIVE' })}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => confirm(`position ${position.code}`, () => deleteMut.mutate())}
                  className="text-destructive"
                >Delete position</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          {(['overview', 'roles', 'files'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === t ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'overview' && 'Overview'}
              {t === 'roles' && (
                <span className="flex items-center gap-1.5">
                  Roles
                  {(position.roles ?? []).length > 0 && (
                    <span className="text-xs bg-blue-100 text-blue-700 rounded-full px-1.5 py-0.5 font-semibold leading-none">
                      {position.roles.length}
                    </span>
                  )}
                </span>
              )}
              {t === 'files' && 'Files'}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'overview' && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Details">
          <dl className="grid grid-cols-[160px_1fr] gap-x-6 gap-y-4 text-sm">
            <Row label="Code">
              <InlineText
                value={position.code}
                onSave={(v) => save({ code: v.toUpperCase() })}
              />
            </Row>
            <Row label="Title">
              <InlineText value={position.title} onSave={(v) => save({ title: v })} />
            </Row>
            <Row label="Job profile">
              <InlineSelect
                value={position.job_profile ? String(position.job_profile) : ''}
                displayLabel={
                  position.job_profile_name
                    ? `${position.job_profile_code} — ${position.job_profile_name}`
                    : <Dash />
                }
                allowClear
                options={jobProfiles.map((j) => ({
                  value: String(j.id), label: `${j.code} — ${j.name}`, searchText: `${j.code} ${j.name}`,
                }))}
                onSave={(v) => save({ job_profile: v ? Number(v) : null })}
              />
            </Row>
            <Row label="Department">
              <InlineSelect
                value={position.department ? String(position.department) : ''}
                displayLabel={position.department_name || <Dash />}
                allowClear
                options={departments.map((d) => ({
                  value: String(d.id), label: `${d.code} ${d.name}`, searchText: `${d.code} ${d.name}`,
                }))}
                onSave={(v) => save({ department: v ? Number(v) : null })}
              />
            </Row>
            <Row label="Subsidiary">
              {subsidiaries.length === 0 ? (
                <div className="text-sm">
                  <span className="text-muted-foreground">Group entity</span>
                  <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-semibold">
                    Single Company mode
                  </span>
                  <div className="text-xs text-muted-foreground mt-1">
                    No subsidiaries defined. Edit the legal entity on the{' '}
                    <Link to="/admin/organization" className="text-blue-600 hover:underline">
                      Organization page
                    </Link>.
                  </div>
                </div>
              ) : (
                <InlineSelect
                  value={position.subsidiary ? String(position.subsidiary) : ''}
                  displayLabel={
                    position.subsidiary_name
                      ? `${position.subsidiary_code} — ${position.subsidiary_name}`
                      : <Dash />
                  }
                  allowClear
                  options={subsidiaries.map((s) => ({
                    value: String(s.id), label: `${s.code} — ${s.name}`, searchText: `${s.code} ${s.name}`,
                  }))}
                  onSave={(v) => save({ subsidiary: v ? Number(v) : null })}
                />
              )}
            </Row>
          </dl>
        </Card>

        <Card title="Org chart">
          <dl className="grid grid-cols-[160px_1fr] gap-x-6 gap-y-4 text-sm">
            <Row label="Reports to">
              <InlineSelect
                value={position.manager_position ? String(position.manager_position) : ''}
                displayLabel={
                  position.manager_position_title
                    ? `${position.manager_position_code} — ${position.manager_position_title}`
                    : <Dash />
                }
                allowClear
                options={parentOptions.map((p) => ({
                  value: String(p.id), label: `${p.code} — ${p.title}`, searchText: `${p.code} ${p.title}`,
                }))}
                onSave={(v) => save({ manager_position: v ? Number(v) : null })}
              />
            </Row>
            <Row label="Direct reports">
              {directReports.length === 0 ? (
                <Dash />
              ) : (
                <ul className="space-y-1">
                  {directReports.map((p) => (
                    <li key={p.id}>
                      <Link to={`/admin/hr/positions/${p.id}`} className="text-blue-600 hover:underline">
                        {p.code} — {p.title}
                      </Link>
                      {p.current_holder_name && (
                        <span className="text-xs text-muted-foreground"> · {p.current_holder_name}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Row>
            <Row label="Planned start">
              <InlineDate value={position.planned_start_date} onSave={(v) => save({ planned_start_date: v })} />
            </Row>
            <Row label="Planned end">
              <InlineDate value={position.planned_end_date} onSave={(v) => save({ planned_end_date: v })} />
            </Row>
          </dl>
        </Card>

        <Card title="Notes" className="lg:col-span-2">
          <InlineText
            value={position.notes}
            multiline
            onSave={(v) => save({ notes: v })}
            placeholder="Notes about this position…"
          />
        </Card>
      </div>
      )}
      {tab === 'roles' && (
        <RolesTab position={position} positionId={positionId} />
      )}
      {tab === 'files' && <FilesTab parent="hr.position" parentId={positionId} />}
    </AdminLayout>
  )
}

// ─────────────────── Roles tab ───────────────────

function RolesTab({ position, positionId }: { position: Position; positionId: number }) {
  const qc = useQueryClient()

  // All system roles for the picker
  const rolesQ = useQuery({
    queryKey: ['core', 'roles'],
    queryFn: async () => (await api.get<RoleSummary[]>('/roles/')).data,
  })

  const [adding, setAdding] = useState(false)
  const [selectedId, setSelectedId] = useState('')

  const assignedIds = new Set(position.roles ?? [])

  // Roles not yet on this position
  const available = (rolesQ.data ?? []).filter((r) => !assignedIds.has(r.id))

  const addMut = useMutation({
    mutationFn: async (roleId: number) =>
      (await api.patch(`/hr/positions/${positionId}/`, {
        role_ids: [...(position.roles ?? []), roleId],
      })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'position', positionId] })
      qc.invalidateQueries({ queryKey: ['hr', 'positions'] })
      toast.success('Role added to position')
      setAdding(false)
      setSelectedId('')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const removeMut = useMutation({
    mutationFn: async (roleId: number) =>
      (await api.patch(`/hr/positions/${positionId}/`, {
        role_ids: (position.roles ?? []).filter((id) => id !== roleId),
      })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'position', positionId] })
      qc.invalidateQueries({ queryKey: ['hr', 'positions'] })
      toast.success('Role removed from position')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const assignedRoles = (position.roles_detail ?? [])

  return (
    <div className="max-w-2xl space-y-4">
      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-800">
        Roles configured here are automatically granted to whoever is hired into this position.
        When the incumbent changes, role membership is resynced instantly.
      </div>

      {/* Roles table / empty state */}
      <div className="bg-card rounded-lg shadow overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h3 className="font-semibold text-sm">Default Roles</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {assignedRoles.length === 0
                ? 'No roles configured. Add one to grant permissions to the holder of this seat.'
                : `${assignedRoles.length} role${assignedRoles.length !== 1 ? 's' : ''} — granted to the current and future incumbent.`}
            </p>
          </div>
          <Button size="sm" onClick={() => setAdding(true)}>
            <Plus className="h-3 w-3 mr-1" /> Add role
          </Button>
        </div>

        {assignedRoles.length === 0 ? (
          <div className="px-6 py-10 text-center text-sm text-muted-foreground italic">
            No roles assigned to this position yet.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Role</Th>
                <Th>Permissions</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {assignedRoles.map((role) => (
                <tr key={role.id}>
                  <Td>
                    <div className="font-medium text-sm">{role.name}</div>
                    {role.description && (
                      <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                        {role.description}
                      </div>
                    )}
                  </Td>
                  <Td>
                    <div className="flex flex-wrap gap-1">
                      {role.permission_codes.slice(0, 6).map((code) => (
                        <span
                          key={code}
                          className="text-xs bg-muted rounded px-1.5 py-0.5 font-mono"
                        >
                          {code}
                        </span>
                      ))}
                      {role.permission_codes.length > 6 && (
                        <span className="text-xs text-muted-foreground">
                          +{role.permission_codes.length - 6} more
                        </span>
                      )}
                      {role.permission_codes.length === 0 && (
                        <span className="text-xs text-muted-foreground italic">No permissions</span>
                      )}
                    </div>
                  </Td>
                  <Td className="text-right">
                    <button
                      type="button"
                      onClick={() => removeMut.mutate(role.id)}
                      disabled={removeMut.isPending}
                      className="text-xs text-destructive hover:underline disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add role drawer */}
      <RecordDrawer
        open={adding}
        onOpenChange={(v) => {
          if (!v) { setAdding(false); setSelectedId('') }
        }}
        title="Add role to position"
        description="Select a system role to grant to whoever fills this seat. The role will be applied to the current incumbent immediately."
        onSubmit={() => { if (selectedId) addMut.mutate(Number(selectedId)) }}
        isPending={addMut.isPending}
        submitLabel="Add role"
      >
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1.5 block">Role</label>
            <SearchableSelect
              value={selectedId}
              onChange={setSelectedId}
              placeholder="Search roles..."
              options={available.map((r) => ({
                value: String(r.id),
                label: r.name,
                hint: r.description || undefined,
                keywords: r.permission_codes.join(' '),
              }))}
              emptyLabel={rolesQ.isLoading ? 'Loading...' : 'No more roles available'}
            />
          </div>

          {/* Permission preview */}
          {selectedId && (() => {
            const role = available.find((r) => r.id === Number(selectedId))
            return role && role.permission_codes.length > 0 ? (
              <div className="rounded border border-border p-3 bg-muted/30">
                <p className="text-xs font-medium text-muted-foreground mb-1.5">
                  Permissions this role grants
                </p>
                <div className="flex flex-wrap gap-1">
                  {role.permission_codes.map((code) => (
                    <span
                      key={code}
                      className="text-xs bg-card rounded px-1.5 py-0.5 font-mono border border-border"
                    >
                      {code}
                    </span>
                  ))}
                </div>
              </div>
            ) : null
          })()}
        </div>
      </RecordDrawer>
    </div>
  )
}

function Th({ children }: { children?: ReactNode }) {
  return (
    <th className="px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">
      {children}
    </th>
  )
}
function Td({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`}>{children}</td>
}

function Card({ title, children, className = '' }: { title: string; children: ReactNode; className?: string }) {
  return (
    <div className={`bg-card rounded-lg shadow p-6 ${className}`}>
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">{title}</h3>
      {children}
    </div>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}
